import ArrowLeftIcon from "@/assets/icons/ArrowLeft";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { Box } from "@/components/ui/box";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAccount } from "@/hooks/useAccount";
import { useAuth } from "@/hooks/useAuth";
import { updateAppUser } from "@/services/appuser.queries";
import { useToastNotify } from "@/stores/toastStore";
import { formatPhoneNumber, getPhoneDigits } from "@/utils/formatPhoneNumber";
import { profileSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { T, useT } from "@transifex/react";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, Form, useForm } from "react-hook-form";
import { Platform } from "react-native";
import type { z } from "zod";

type ProfileSchemaType = z.infer<typeof profileSchema>;

const ProfileSkeleton = () => (
  <Screen>
    <VStack className="gap-[32px]">
      <Box className="items-center">
        <SkeletonText className="h-[30px] w-[80px] rounded-md" />
      </Box>
      <VStack className="gap-[20px]">
        <SkeletonText className="h-[20px] w-[100px] rounded-sm" />
        <Skeleton className="h-[52px] w-full rounded-md" />
        <SkeletonText className="h-[20px] w-[100px] rounded-sm" />
        <Skeleton className="h-[52px] w-full rounded-md" />
        <SkeletonText className="h-[20px] w-[100px] rounded-sm" />
        <Skeleton className="h-[52px] w-full rounded-md" />
        <SkeletonText className="h-[20px] w-[100px] rounded-sm" />
        <Skeleton className="h-[52px] w-full rounded-md" />
      </VStack>
      <HStack space="md" className="gap-[12px]">
        <Skeleton className="h-[52px] flex-1 rounded-md" />
        <Skeleton className="h-[52px] flex-1 rounded-md" />
      </HStack>
    </VStack>
  </Screen>
);

export default function Profile() {
  const { account, isFetching } = useAccount();
  const { notify } = useToastNotify();
  const queryClient = useQueryClient();
  const { user, update } = useAuth();
  const t = useT();

  const defaultValues = useMemo(
    () => ({
      first: account?.first ?? "",
      last: account?.last ?? "",
      email: account?.email ?? "",
      phone: account?.phone ?? "",
    }),
    [account],
  );

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<ProfileSchemaType>({
    resolver: zodResolver(profileSchema),
    defaultValues,
  });

  // Update form when account data loads
  useEffect(() => {
    if (account) {
      reset(defaultValues);
    }
  }, [account, reset, defaultValues]);

  const accountMutation = useMutation({
    mutationFn: updateAppUser,
    onError: (error) => {
      notify({
        severity: "error",
        message: t("An error occurred when updating user data"),
      });
      console.error("Error updating user data: ", error);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["account"] });
    },
  });

  const onSubmit = useCallback(
    (data: ProfileSchemaType) => {
      if (account) {
        accountMutation.mutateAsync({
          ...account,
          cognitoIDToken: user?.idToken ?? "",
          email: data.email,
          first: data.first,
          last: data.last,
          phone: data.phone ? `+${getPhoneDigits(data.phone)}` : "",
        });
        update(data.email, data.first, data.last, data.phone ?? null);
      }
    },
    [account, accountMutation, user?.idToken, update],
  );

  if (isFetching) {
    return <ProfileSkeleton />;
  }

  return (
    <ProtectedRoute>
      <Screen>
        <VStack className="gap-[32px]">
          <PageTitle title={t("Profile")} />
          <Form control={control} onSubmit={() => handleSubmit(onSubmit)()}>
            <VStack className="gap-[20px]">
              <FormControl isDisabled={accountMutation.isPending} isInvalid={!!errors.first} key="first-name-input" className="gap-[10px]">
                <FormControlLabel>
                  <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
                    <T _str="First name" />
                  </FormControlLabelText>
                </FormControlLabel>
                <Controller
                  name="first"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.first ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
                    >
                      <InputField
                        placeholder={t("Enter Your First Name")}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        onFocus={() => clearErrors("first")}
                        accessibilityLabel={t("First Name")}
                        className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                      />
                    </Input>
                  )}
                />
                <FormControlError>
                  <FormControlErrorText className="text-xs font-semibold text-error-100">{errors.first?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>

              <FormControl isDisabled={accountMutation.isPending} isInvalid={!!errors.last} key="last-name-input" className="gap-[10px]">
                <FormControlLabel>
                  <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
                    <T _str="Last name" />
                  </FormControlLabelText>
                </FormControlLabel>
                <Controller
                  name="last"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.last ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
                    >
                      <InputField
                        placeholder={t("Enter Your Last Name")}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        onFocus={() => clearErrors("last")}
                        accessibilityLabel={t("Last Name")}
                        className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                      />
                    </Input>
                  )}
                />
                <FormControlError>
                  <FormControlErrorText className="text-xs font-semibold text-error-100">{errors.last?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>

              <FormControl isDisabled={accountMutation.isPending} isInvalid={!!errors.phone} key="phone-input" className="gap-[10px]">
                <FormControlLabel>
                  <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
                    <T _str="Phone number" />
                  </FormControlLabelText>
                </FormControlLabel>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.phone ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
                    >
                      <InputField
                        placeholder={t("Enter Your Phone Number")}
                        value={typeof value === "string" ? formatPhoneNumber(value) : ""}
                        onChangeText={(text: string) => onChange(getPhoneDigits(text))}
                        onBlur={onBlur}
                        onFocus={() => clearErrors("phone")}
                        keyboardType="phone-pad"
                        returnKeyType="next"
                        accessibilityLabel={t("Phone Number")}
                        className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                      />
                    </Input>
                  )}
                />
                <FormControlError>
                  <FormControlErrorText className="text-xs font-semibold text-error-100">{errors.phone?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>

              <FormControl isInvalid={!!errors.email} key="email-input" className="gap-[10px]">
                <FormControlLabel className="flex flex-col items-start gap-1">
                  <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
                    <T _str="Email" />
                  </FormControlLabelText>
                  <Text className="text-typography-900 text-[14px] leading-[20px] font-[400]">
                    <T _str="Email cannot be edited at this time." />
                  </Text>
                </FormControlLabel>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      isDisabled={true}
                      className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.email ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
                    >
                      <InputField
                        placeholder={t("Enter Your Email Address")}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        onFocus={() => clearErrors("email")}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        returnKeyType="next"
                        accessibilityLabel={t("Email Address")}
                        className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                      />
                    </Input>
                  )}
                />
                <FormControlError>
                  <FormControlErrorText className="text-xs font-semibold text-error-100">{errors.email?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>
            </VStack>
            {Platform.OS === "web" && <button className="hidden" type="submit" />}
          </Form>
          <HStack space="md" className="gap-[12px]">
            <AppButton
              className="flex-1"
              disabled={accountMutation.isPending || !router.canGoBack()}
              onPress={() => {
                if (!router.canGoBack()) {
                  router.replace("/welcome");
                } else {
                  router.back();
                }
              }}
              title={t("Back")}
              icon={ArrowLeftIcon}
              variant="outline"
            />
            <AppButton className="flex-1" loading={accountMutation.isPending} onPress={handleSubmit(onSubmit)} title={t("Save")} />
          </HStack>
        </VStack>
      </Screen>
    </ProtectedRoute>
  );
}
